/**
 * P3.8 follow-through — evidence probes for EVIDENCE-PROBED checklist rows.
 *
 * Config (`reportSetupRequirements.ts`) names a probe by id only; this module
 * binds the id to the function, so the config layer never imports the probe's
 * dependency chain (the sentiment probe reaches the sentiment orchestrator and
 * the audit-log client). Consumers on the coverage surfaces call
 * `evidenceProbeFor(item)`; an item may also carry an inline `evidenceProbe`
 * (tests, ad-hoc rows), which wins.
 */
import type { ChecklistItem, EvidenceProbeId, EvidenceProbeVerdict } from '../../config/documentChecklists';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import { publicSentimentScreeningReadiness } from '../sentiment/publicSentimentScreening';

export type EvidenceProbe = (sed: SiteEvidenceDocument | null | undefined) => EvidenceProbeVerdict;

/** The chokepoint's own verdict (`probePublicSentimentEvidence`), in the user's terms. */
export function publicSentimentScanProbe(sed: SiteEvidenceDocument | null | undefined): EvidenceProbeVerdict {
  const readiness = publicSentimentScreeningReadiness(sed);
  return readiness.ready ? { status: 'strong' } : { status: 'missing', reason: readiness.reason };
}

const EVIDENCE_PROBES: Record<EvidenceProbeId, EvidenceProbe> = {
  'public-sentiment-scan': publicSentimentScanProbe,
};

export function evidenceProbeFor(
  item: Pick<ChecklistItem, 'evidenceProbe' | 'evidenceProbeId'> | null | undefined,
): EvidenceProbe | null {
  if (!item) return null;
  if (item.evidenceProbe) return item.evidenceProbe;
  return item.evidenceProbeId ? EVIDENCE_PROBES[item.evidenceProbeId] ?? null : null;
}
